import { useEffect, useState } from "react";

export interface Result {
  /** Short link the PNG is served under. */
  imageUrl: string;
  /** Short link the code encodes, which redirects to the destination. */
  shortUrl: string;
  /** Where the code ultimately sends a scanner. */
  targetUrl: string;
  /** How long the image link lasts, already in words. */
  expiry: string;
  /** The image width in pixels, or null while the server config is unknown. */
  size: number | null;
}

interface Props {
  result: Result;
}

export function QrResult({ result }: Props) {
  // Which link was copied last, so the confirmation lands on the right button.
  const [copied, setCopied] = useState<"short" | "image" | null>(null);

  // Clear the confirmation after a moment, and on unmount, so the timer never
  // fires against a component that is gone.
  useEffect(() => {
    if (copied === null) return;

    const timer = window.setTimeout(() => setCopied(null), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function copy(value: string, which: "short" | "image") {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
    } catch {
      // Clipboard access can be refused; the link is selectable either way.
      setCopied(null);
    }
  }

  return (
    <section className="card result" aria-live="polite">
      <div className="qr-frame">
        <img src={result.imageUrl} alt={`QR code for ${result.targetUrl}`} width={256} height={256} />
      </div>

      <dl className="meta">
        <dt>Scans to</dt>
        <dd className="ellipsis" title={result.targetUrl}>
          {result.targetUrl}
        </dd>
        <dt>Image expires</dt>
        <dd>{result.expiry}</dd>
        {result.size !== null && (
          <>
            <dt>Size</dt>
            <dd>
              {result.size} × {result.size} px
            </dd>
          </>
        )}
      </dl>

      <div className="links">
        <div className="link-block">
          <label className="link-row">
            <span className="link-label">Short link (what the code contains)</span>
            <input value={result.shortUrl} readOnly onFocus={(event) => event.target.select()} />
          </label>
          <button type="button" onClick={() => copy(result.shortUrl, "short")}>
            {copied === "short" ? "Copied" : "Copy"}
          </button>
        </div>

        <div className="link-block">
          <label className="link-row">
            <span className="link-label">Image link</span>
            <input value={result.imageUrl} readOnly onFocus={(event) => event.target.select()} />
          </label>
          <button type="button" onClick={() => copy(result.imageUrl, "image")}>
            {copied === "image" ? "Copied" : "Copy"}
          </button>
        </div>
      </div>

      <div className="actions">
        <a className="button" href={result.imageUrl} download target="_blank" rel="noreferrer">
          Download image
        </a>
        <a className="button" href={result.shortUrl} target="_blank" rel="noreferrer">
          Test the link
        </a>
      </div>
    </section>
  );
}
